"use client";
import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Footer } from '../components/landing/Footer';
import { useTranslation } from '../hooks/useTranslation';
import { HeroContent } from '../components/landing/HeroContent';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { cn } from '../utils/classNames';
import { pricingTranslations } from '../i18n/translations/pages';

export type Tier = {
  name: string;
  id: string;
  price: string;
  description: string;
  features: string[];
  featured: boolean;
  cta: string;
  onClick: () => void;
};

const PricingPage: React.FC = () => {
  const { locale } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  
  // Get translations for current language, fallback to English if not available
  const translations = pricingTranslations[locale as keyof typeof pricingTranslations] || pricingTranslations.en;
  
  const tiers: Tier[] = [
    {
      name: translations.tiers.free.name,
      id: "tier-free",
      price: translations.tiers.free.price,
      description: translations.tiers.free.description,
      features: translations.tiers.free.features,
      featured: true,
      cta: translations.tiers.free.cta,
      onClick: () => {
        console.log("Get Free Plan");
        // Navigate to homepage
        window.location.href = "/";
      },
    },
    {
      name: translations.tiers.business.name,
      id: "tier-business",
      price: translations.tiers.business.price,
      description: translations.tiers.business.description,
      features: translations.tiers.business.features,
      featured: false,
      cta: translations.tiers.business.cta,
      onClick: () => {
        console.log("Get Paid Plan");
        // Navigate to contact page
        window.location.href = "/contact";
      },
    },
  ];
  
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* Pricing Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              {translations.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              {translations.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Pricing Content */}
      <div className="w-full py-16" id="pricing">
        <PricingSection tiers={tiers} />
      </div>
      <Footer />
    </div>
  );
};

function PricingSection({ tiers }: { tiers: Tier[] }) {
  return (
    <div className="relative pb-10 md:pb-20 max-w-7xl mx-auto flex flex-col items-center justify-between">
      <Pricing tiers={tiers} />
    </div>
  );
}

function Pricing({ tiers }: { tiers: Tier[] }) {
  return (
    <div className="relative">
      <div className="mx-auto mt-4 md:mt-20 grid relative z-20 grid-cols-1 gap-4 items-center md:grid-cols-2">
        {tiers.map((tier, tierIdx) => (
          <div
            key={tier.id}
            className={cn(
              tier.featured
                ? "relative bg-[radial-gradient(164.75%_100%_at_50%_0%,#334155_0%,#0F172A_48.73%)] shadow-2xl"
                : "bg-white dark:bg-black",
              "rounded-lg px-6 py-8 sm:mx-8 lg:mx-0 h-full flex flex-col justify-between"
            )}
          >
            <div>
              <h3
                id={tier.id}
                className={cn(
                  tier.featured
                    ? "text-white"
                    : "text-neutral-700 dark:text-neutral-400",
                  "text-base font-semibold leading-7"
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4">
                <motion.span
                  initial={{ y: 20, opacity: 0, filter: "blur(10px)" }}
                  animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.2,
                    ease: "easeOut",
                    delay: 0.1 * tierIdx,
                  }}
                  className={cn(
                    "text-4xl font-bold tracking-tight inline-block",
                    tier.featured
                      ? "text-white"
                      : "text-neutral-900 dark:text-neutral-200"
                  )}
                >
                  {tier.price}
                </motion.span>
              </p>
              <p
                className={cn(
                  tier.featured
                    ? "text-neutral-300"
                    : "text-neutral-600 dark:text-neutral-300",
                  "mt-6 text-sm leading-7 h-12 md:h-12 xl:h-12"
                )}
              >
                {tier.description}
              </p>
              <ul
                role="list"
                className={cn(
                  tier.featured
                    ? "text-neutral-300"
                    : "text-neutral-600 dark:text-neutral-300",
                  "mt-8 space-y-3 text-sm leading-6 sm:mt-10"
                )}
              >
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <Check
                      className={cn(
                        tier.featured
                          ? "text-white"
                          : "text-neutral-700 dark:text-neutral-400",
                        "h-6 w-5 flex-none"
                      )}
                      aria-hidden="true"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <button
                onClick={tier.onClick}
                aria-describedby={tier.id}
                className={cn(
                  "mt-8 bg-black text-white hover:ring-2 ring-offset-2 dark:ring-offset-2 transition duration-200 ring-black dark:ring-white dark:text-black dark:bg-white rounded-full py-2.5 px-3.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10 block w-full",
                  tier.featured
                    ? "bg-white dark:bg-white text-black shadow-sm hover:bg-white/90 focus-visible:outline-white"
                    : ""
                )}
              >
                {tier.cta}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PricingPage; 